import { useLocale, useTranslate } from '../../application/i18n/LocaleProvider';
import { carName } from '../../domain/cars/carCatalogue';
import { HERO_TAKE, heroTake } from '../../domain/cars/carSchema';
import { formatSpecNumber } from '../../domain/cars/carProperties';
import { artSource } from '../cars/carArt';
import { SERIES_CLASS } from './comparePlot';

/**
 * The race, watched (SPEC-008 PR-8.5).
 *
 * Two lanes, both cars in SIDE PROFILE, a start line at the left and 400 metres
 * at the right. The side profile is the product's own picture and it is the only
 * take that can be moved along a strip: a front view driven sideways across a
 * screen is a car facing the reader while travelling left to right, which reads
 * as a mistake in a way no caption recovers from. The two cars with no side
 * profile fall back through `heroTake` to their front view and are drawn
 * face-on, which is the honest degradation — the alternative is excluding them
 * from the one screen that exists to compare any two cars.
 *
 * EVERYTHING ON THIS STRIP IS A FUNCTION OF ONE NUMBER — the clock's simulated
 * seconds — so the position, the speed readout, the gear and the wheelspin
 * cannot disagree with each other. That is the same argument the duel's timer
 * makes for decrementing its own counter rather than reading a clock twice.
 *
 * The horizontal position is a `translate` on a full-width CARRIAGE, so the
 * browser moves it on the compositor and the strip does not relayout twenty
 * times a second on a phone. It is not a CSS transition: the timing is the
 * physics, and a transition would interpolate between the frames the physics
 * already produced — smoothing a curve that is already the answer.
 *
 * IT IS DELIBERATELY SHORT. The two speed curves used to be drawn behind these
 * lanes and are now a plate of their own beneath them (`RaceChart.js`), which is
 * what lets this one be two rows per lane instead of three — and the two of them
 * fit on a phone together, which neither could while one was hiding inside the
 * other.
 */

/**
 * How much of the lane a car's own picture takes up.
 *
 * The car is drawn at a fixed width and travels between the start line and the
 * finish, so its NOSE reaches the line rather than its tail — which is what a
 * finish line means. Without this the artwork's left edge would arrive at 400
 * metres and the whole car would still be short of it.
 */
const CAR_WIDTH_PERCENT = 22;

function Lane({ car, isWinner, lane, position, revealed }) {
    const t = useTranslate();
    const { languageCode } = useLocale();
    const art = car ? artSource(car, heroTake(car)) : null;
    const travel = position ? position.share * (100 - CAR_WIDTH_PERCENT) : 0;
    const classes = [
        'race-lane',
        position?.wheelspin ? 'race-lane--spinning' : '',
        revealed && isWinner ? 'race-lane--winner' : '',
        position?.arrived ? 'race-lane--arrived' : '',
    ].filter(Boolean).join(' ');

    return (
        <div className={classes}>
            {/* ONE header row per lane, carrying the name and whichever figure
                is true at this moment: the live speed while the cars are moving,
                the finishing time and trap speed once they have stopped. It was
                two rows — a speed above the track and a result below it — and
                the row below was empty of anything worth reading for eleven of
                the twelve seconds it was on screen, which is most of the height
                that stopped the strip and its chart sharing a phone. */}
            <p className="race-lane__name">
                <span className="race-lane__car">{carName(car)}</span>
                <span className="race-lane__readout">
                    {(() => {
                        if (!revealed) {
                            return (
                                <>
                                    <span className="race-lane__speed">
                                        {formatSpecNumber(position?.kmh ?? 0, { languageCode })}
                                    </span>
                                    <span className="race-lane__unit">{t('cars.spec.topSpeed.unit')}</span>
                                </>
                            );
                        }

                        return (
                            <span className="race-lane__result">
                                {lane.finished
                                    ? t('compare.race.laneResult', {
                                        seconds: formatSpecNumber(lane.seconds, { languageCode, fractionDigits: 2 }),
                                        speed: formatSpecNumber(lane.trapKmh, { languageCode }),
                                    })
                                    : t('compare.race.laneUnfinished', {
                                        metres: formatSpecNumber(lane.metres, { languageCode }),
                                    })}
                            </span>
                        );
                    })()}
                </span>
            </p>

            <div className="race-lane__track">
                <span className="race-lane__surface" aria-hidden="true" />

                {/* The CARRIAGE is what moves, and it is exactly as wide as the
                    track. That is not a spare element: a percentage in
                    `translateX` resolves against the moving element's OWN width,
                    so translating the car itself by 78% moves it 78% of a car
                    rather than 78% of the strip — a bug that looks like a
                    plausible slow race rather than like a mistake. Translating a
                    full-width carriage makes the percentage mean what it reads
                    as, and the transform still runs on the compositor. */}
                <span
                    className="race-lane__carriage"
                    style={{ transform: `translateX(${travel.toFixed(2)}%)` }}
                >
                    <span className="race-lane__car-mark">
                        {art ? (
                            <img
                                className={`race-lane__art${art.take === HERO_TAKE ? '' : ' race-lane__art--front'}`}
                                src={art.src}
                                srcSet={art.srcSet}
                                sizes="(min-width: 640px) 200px, 30vw"
                                alt={t('a11y.carArtwork', { car: carName(car) })}
                                decoding="async"
                            />
                        ) : null}
                        {/* The spinning mark rides with the car rather than
                            sitting under the lane: it is a fact about this car
                            at this instant, and a badge parked on the track
                            would read as a property of the road. */}
                        <span className="race-lane__spin" aria-hidden="true" />
                    </span>
                </span>
            </div>

        </div>
    );
}

/**
 * How far apart they are, in both of the units that matter.
 *
 * TWO FIGURES WITH TWO LEADERS, not one verdict, because the interesting moment
 * in every close race is exactly the one where they disagree: a car twelve
 * metres behind and fourteen km/h faster is a car catching, and a single "who is
 * winning" would have to throw one of those away.
 *
 * Which car each figure belongs to is a SWATCH in the series colour, matching
 * the curve behind it and the lane beside it — the same two colours doing the
 * same job in three places, so the reader learns them once.
 *
 * Not a live region. It changes twenty times a second, and the verdict below is
 * already `aria-live` and already carries the margin in both units. It stays in
 * the accessibility tree rather than being hidden, so a reader who navigates to
 * it gets the gap as it stands at that moment, which is true.
 */
function RaceGap({ gap, lanes }) {
    const t = useTranslate();
    const { languageCode } = useLocale();

    if (!gap) {
        return null;
    }

    // Which of the two lanes a car is in, so its swatch is the colour its curve
    // and its lane already are.
    const seriesFor = (carId) => SERIES_CLASS[lanes.findIndex((lane) => lane.carId === carId)] ?? '';

    const figures = [
        {
            key: 'distance',
            labelKey: 'compare.race.gapDistance',
            leader: gap.metresLeader,
            text: t('compare.race.gapMetres', {
                metres: formatSpecNumber(gap.metres, { languageCode }),
            }),
        },
        {
            key: 'speed',
            labelKey: 'compare.race.gapSpeed',
            leader: gap.kmhLeader,
            text: t('compare.race.gapKmh', {
                speed: formatSpecNumber(gap.kmh, { languageCode }),
            }),
        },
    ];

    return (
        <p className="race-gap">
            {figures.map((figure) => (
                <span className="race-gap__figure" key={figure.key}>
                    <span className="race-gap__label">{t(figure.labelKey)}</span>
                    {figure.leader === null ? (
                        <span className="race-gap__level">{t('compare.race.gapLevel')}</span>
                    ) : (
                        <span className="race-gap__value">
                            <span
                                className={`race-gap__swatch ${seriesFor(figure.leader)}`}
                                aria-hidden="true"
                            />
                            {figure.text}
                        </span>
                    )}
                </span>
            ))}
        </p>
    );
}

/**
 * The sentence a freeze is stopped for.
 *
 * It lives INSIDE the strip plate and is always in the document, empty or not,
 * so its two lines of height are reserved from the first render. A caption that
 * appeared only while held would push the charts below it down three times a
 * race, under a reader who is watching something move.
 *
 * `aria-live` is `polite` and the region is otherwise silent: the three moments
 * are the only things it ever says, and each is said once.
 */
function RaceMoment({ carName: name, moment }) {
    const t = useTranslate();

    return (
        <p className="race-strip__moment" aria-live="polite">
            {moment ? t(`compare.moment.${moment.key}`, { car: name, ...moment.values }) : ''}
        </p>
    );
}

export default function DragRaceStrip({ cars, gap, moment, positions, race, revealed }) {
    const t = useTranslate();
    const { languageCode } = useLocale();
    const momentCar = moment ? cars[race.lanes.findIndex((lane) => lane.carId === moment.carId)] : null;

    return (
        <div className={`race-strip${moment ? ' race-strip--held' : ''}`}>
            <p className="race-strip__scale" aria-hidden="true">
                <span>{t('compare.race.start')}</span>
                <span>
                    {t('compare.race.finish', {
                        metres: formatSpecNumber(race.distance, { languageCode }),
                    })}
                </span>
            </p>

            <div className="race-strip__lanes">
                <Lane
                    car={cars[0]}
                    lane={race.lanes[0]}
                    position={positions[0]}
                    isWinner={race.winner === race.lanes[0].carId}
                    revealed={revealed}
                />

                <RaceGap gap={gap} lanes={race.lanes} />

                <Lane
                    car={cars[1]}
                    lane={race.lanes[1]}
                    position={positions[1]}
                    isWinner={race.winner === race.lanes[1].carId}
                    revealed={revealed}
                />
            </div>

            <RaceMoment moment={moment} carName={momentCar ? carName(momentCar) : ''} />
        </div>
    );
}
